import PropTypes from 'prop-types';
import Link from 'next/link';
import Head from 'next/head';
import classNames from 'classnames';

const LEFT_PAGE = 'LEFT';
const RIGHT_PAGE = 'RIGHT';

const range = (from, to, step = 1) => {
  let i = from;
  const list = [];

  while (i <= to) {
    list.push(i);
    i += step;
  }

  return list;
};

const Pagination = ({ title, currentPage, totalPages, base }) => {
  let previousPage;
  let nextPage;

  if (currentPage === totalPages) {
    previousPage = `${base}/`;
    nextPage = `${base}/`;
  } else if (currentPage <= 2) {
    previousPage = `${base}/`;
    nextPage = `${base}/page/${currentPage + 1}`;
  } else if (currentPage === totalPages) {
    previousPage = `${base}/page/${currentPage - 1}`;
    nextPage = `${base}/`;
  } else {
    previousPage = `${base}/page/${currentPage - 1}`;
    nextPage = `${base}/page/${currentPage + 1}`;
  }

  if (title && currentPage !== 1) {
    const titleArr = title.split('|');
    titleArr.splice(1, 0, ` Page ${currentPage} of ${totalPages} `);
    title = titleArr.join('|');
  }

  const fetchPageNumbers = () => {
    const pageNeighbors = 2;

    /**
     * totalNumbers: the total page numbers to show on the control
     * totalBlocks: totalNumbers + 2 to cover for the left(<) and right(>) controls
     */
    const totalNumbers = pageNeighbors * 2 + 3;
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - pageNeighbors);
      const endPage = Math.min(totalPages - 1, currentPage + pageNeighbors);
      let pages = range(startPage, endPage);

      /**
       * hasLeftSpill: has hidden pages to the left
       * hasRightSpill: has hidden pages to the right
       * spillOffset: number of hidden pages either to the left or to the right
       */
      const hasLeftSpill = startPage > 2;
      const hasRightSpill = totalPages - endPage > 1;
      const spillOffset = totalNumbers - (pages.length + 1);

      switch (true) {
        // handle: (1) < {5 6} [7] {8 9} (10)
        case hasLeftSpill && !hasRightSpill: {
          const extraPages = range(startPage - spillOffset, startPage - 1);
          pages = [LEFT_PAGE, ...extraPages, ...pages];
          break;
        }

        // handle: (1) {2 3} [4] {5 6} > (10)
        case !hasLeftSpill && hasRightSpill: {
          const extraPages = range(endPage + 1, endPage + spillOffset);
          pages = [...pages, ...extraPages, RIGHT_PAGE];
          break;
        }

        // handle: (1) < {4 5} [6] {7 8} > (10)
        case hasLeftSpill && hasRightSpill:
        default: {
          pages = [LEFT_PAGE, ...pages, RIGHT_PAGE];
          break;
        }
      }

      return [1, ...pages, totalPages];
    }

    return range(1, totalPages);
  };

  const pages = fetchPageNumbers();

  return (
    <>
      <Head>
        {title !== '' && (
          <>
            <title>{title}</title>
            <meta property="og:title" content={title} key="ogTitle" />
          </>
        )}
        <Link href={`${previousPage}`} passHref>
          <link rel="previous" />
        </Link>
        <Link href={`${nextPage}`} passHref>
          <link rel="next" />
        </Link>
      </Head>
      <nav className="mt-6 flex items-center justify-between border-t border-gray-200 px-4 sm:px-0">
        <div className="-mt-px flex w-0 flex-1">
          <Link href={`${previousPage}`} passHref>
            <a className="inline-flex items-center border-t-2 border-transparent pt-4 pr-1 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700">
              <svg
                className="mr-3 h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Previous
            </a>
          </Link>
        </div>
        <div className="hidden md:-mt-px md:flex">
          {pages.map((page) => {
            if (page === LEFT_PAGE || page === RIGHT_PAGE) {
              return (
                <span
                  key={page}
                  className="inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500"
                  aria-hidden="true"
                >
                  …
                </span>
              );
            }

            return (
              <Link
                key={page}
                href={page === 1 ? `${base}/` : `${base}/page/${page}`}
                passHref
              >
                <a
                  className={classNames([
                    currentPage !== page &&
                      'inline-flex items-center border-t-2 border-transparent px-4 pt-4 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700',
                    currentPage === page &&
                      'inline-flex items-center border-t-2 border-blue-08 px-4 pt-4 text-sm font-medium text-blue-08',
                  ])}
                  aria-current="page"
                >
                  {page}
                </a>
              </Link>
            );
          })}
        </div>
        <div className="-mt-px flex w-0 flex-1 justify-end">
          <Link href={`${nextPage}`} passHref>
            <a className="inline-flex items-center border-t-2 border-transparent pt-4 pl-1 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700">
              Next
              <svg
                className="ml-3 h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
          </Link>
        </div>
      </nav>
    </>
  );
};

Pagination.propTypes = {
  title: PropTypes.string,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  base: PropTypes.string,
};

Pagination.defaultProps = {
  title: '',
  base: '',
};

export default Pagination;
